import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";

// ── Constants ──────────────────────────────────────────────────────────────────
const COLS = 13;
const ROWS = 13;
const CELL = 1.0;

// Row types from bottom (row 0) to top (row 12)
// 0   = safe zone (start)
// 1-5 = road (cars)
// 6   = safe median
// 7-11= river (logs/turtles)
// 12  = goal zone

const ROAD_ROWS  = [1, 2, 3, 4, 5];
const RIVER_ROWS = [7, 8, 9, 10, 11];
const GOAL_ROW   = 12;
// const SAFE_ROWS = [0, 6]; // reserved for future use

// Lane configs: { speed, dir, type, color, w, h }
const LANE_CFG = {
  1:  { speed: 0.022, dir:  1, type: "car",   color: 0xff003c, w: 1.8, h: 0.72 },
  2:  { speed: 0.018, dir: -1, type: "car",   color: 0xff6600, w: 1.6, h: 0.72 },
  3:  { speed: 0.028, dir:  1, type: "car",   color: 0xffee00, w: 1.5, h: 0.72 },
  4:  { speed: 0.016, dir: -1, type: "truck", color: 0xff00cc, w: 2.2, h: 0.72 },
  5:  { speed: 0.032, dir:  1, type: "truck", color: 0x00ffee, w: 2.4, h: 0.72 },
  7:  { speed: 0.014, dir:  1, type: "log",   color: 0xb8864e, w: 2.6, h: 0.68 },
  8:  { speed: 0.019, dir: -1, type: "log",   color: 0xc8966e, w: 2.2, h: 0.68 },
  9:  { speed: 0.012, dir:  1, type: "log",   color: 0xb8864e, w: 3.0, h: 0.68 },
  10: { speed: 0.022, dir: -1, type: "log",   color: 0xd4a574, w: 2.4, h: 0.68 },
  11: { speed: 0.015, dir:  1, type: "log",   color: 0xc8966e, w: 2.8, h: 0.68 },
};


// ── Scene builder ──────────────────────────────────────────────────────────────
function buildScene() {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0a0015);

  const W = COLS * CELL;
  const H = ROWS * CELL;
  const hw = W / 2;
  const hh = H / 2;

  // Grid base plane
  const baseGeo = new THREE.PlaneGeometry(W, H);
  const baseMat = new THREE.MeshBasicMaterial({ color: 0x0a0015 });
  const base = new THREE.Mesh(baseGeo, baseMat);
  scene.add(base);

  // Row backgrounds
  for (let r = 0; r < ROWS; r++) {
    const cx = 0;
    const cy = -hh + r * CELL + CELL / 2;
    let color = 0x0c001a;
    if (ROAD_ROWS.includes(r))  color = 0x111128;
    if (RIVER_ROWS.includes(r)) color = 0x001833;
    if (r === 6)                color = 0x0a1a0a;
    if (r === 0)                color = 0x0a1a0a;
    if (r === GOAL_ROW)         color = 0x001a0a;
    const geo = new THREE.PlaneGeometry(W, CELL);
    const mat = new THREE.MeshBasicMaterial({ color });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(cx, cy, 0.01);
    scene.add(mesh);
  }

  // Grid lines
  const lineMat = new THREE.LineBasicMaterial({ color: 0x220044, transparent: true, opacity: 0.4 });
  for (let r = 0; r <= ROWS; r++) {
    const y = -hh + r * CELL;
    const geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-hw, y, 0.02),
      new THREE.Vector3(hw,  y, 0.02),
    ]);
    scene.add(new THREE.Line(geo, lineMat));
  }
  for (let c = 0; c <= COLS; c++) {
    const x = -hw + c * CELL;
    const geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(x, -hh, 0.02),
      new THREE.Vector3(x,  hh, 0.02),
    ]);
    scene.add(new THREE.Line(geo, lineMat));
  }

  // Goal lily pads (5 of them, evenly spaced)
  const pads = [];
  for (let i = 0; i < 5; i++) {
    const gx = -hw + (i * 2 + 1) * CELL + CELL / 2;
    const gy = -hh + GOAL_ROW * CELL + CELL / 2;
    const geo = new THREE.CircleGeometry(CELL * 0.38, 16);
    const mat = new THREE.MeshBasicMaterial({ color: 0x00ff44 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(gx, gy, 0.03);
    scene.add(mesh);

    // Glow ring
    const ring = new THREE.RingGeometry(CELL * 0.36, CELL * 0.44, 16);
    const ringMat = new THREE.MeshBasicMaterial({ color: 0x00ff44, transparent: true, opacity: 0.5, side: THREE.DoubleSide });
    const ringMesh = new THREE.Mesh(ring, ringMat);
    ringMesh.position.set(gx, gy, 0.04);
    scene.add(ringMesh);

    // Mini frog that appears when the pad is occupied
    const frogGroup = new THREE.Group();
    const bodyGeo = new THREE.CircleGeometry(CELL * 0.28, 12);
    const bodyMat = new THREE.MeshBasicMaterial({ color: 0x006622 });
    frogGroup.add(new THREE.Mesh(bodyGeo, bodyMat));
    // Eyes
    const eyeGeo = new THREE.CircleGeometry(CELL * 0.07, 8);
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const eL = new THREE.Mesh(eyeGeo, eyeMat);
    eL.position.set(-CELL * 0.13, CELL * 0.13, 0.01);
    const eR = new THREE.Mesh(eyeGeo, eyeMat.clone());
    eR.position.set(CELL * 0.13, CELL * 0.13, 0.01);
    frogGroup.add(eL, eR);
    // Pupils
    const pupilGeo = new THREE.CircleGeometry(CELL * 0.035, 8);
    const pupilMat = new THREE.MeshBasicMaterial({ color: 0x001100 });
    const pL = new THREE.Mesh(pupilGeo, pupilMat);
    pL.position.set(-CELL * 0.13, CELL * 0.13, 0.02);
    const pR = new THREE.Mesh(pupilGeo, pupilMat.clone());
    pR.position.set(CELL * 0.13, CELL * 0.13, 0.02);
    frogGroup.add(pL, pR);
    frogGroup.position.set(gx, gy, 0.07);
    frogGroup.visible = false;
    scene.add(frogGroup);

    pads.push({ x: gx, y: gy, reached: false, ring: ringMesh, padMesh: mesh, frogling: frogGroup });
  }

  return { scene, pads };
}

// Returns how many obstacles to spawn per lane at the given level.
// Level 1 → 2 (very open), level 2 → 3, level 3 → 4, level 4+ → 5 (dense).
function obstacleCountForLevel(level) {
  return Math.min(1 + level, 5);
}

// ── Vehicle mesh builders ──────────────────────────────────────────────────────

function buildCarMesh(cfg) {
  const { color, w, h, dir } = cfg;
  const front = dir; // +1 right, -1 left
  const group = new THREE.Group();

  // Body
  const bodyMat = new THREE.MeshBasicMaterial({ color });
  group.add(new THREE.Mesh(new THREE.PlaneGeometry(w, h), bodyMat));

  // Roof (slightly darker, centered a bit toward the back)
  const roofW = w * 0.50;
  const roofH = h * 0.44;
  const roofX = -front * w * 0.07;
  const roofColor = new THREE.Color(color).multiplyScalar(0.68);
  const roof = new THREE.Mesh(
    new THREE.PlaneGeometry(roofW, roofH),
    new THREE.MeshBasicMaterial({ color: roofColor })
  );
  roof.position.set(roofX, h * 0.11, 0.01);
  group.add(roof);

  // Windshield (front half of roof, light blue)
  const wsW = roofW * 0.46;
  const wsGeo = new THREE.PlaneGeometry(wsW, roofH * 0.88);
  const wsMat = new THREE.MeshBasicMaterial({ color: 0x88ddff, transparent: true, opacity: 0.82 });
  const ws = new THREE.Mesh(wsGeo, wsMat);
  ws.position.set(roofX + front * roofW * 0.22, h * 0.11, 0.02);
  group.add(ws);

  // Headlights (front, yellow)
  const frontX = front * (w * 0.46);
  const hlGeo = new THREE.CircleGeometry(h * 0.10, 7);
  const hlMat = new THREE.MeshBasicMaterial({ color: 0xffffaa });
  const hlT = new THREE.Mesh(hlGeo, hlMat);
  hlT.position.set(frontX, h * 0.20, 0.03);
  const hlB = new THREE.Mesh(hlGeo, hlMat.clone());
  hlB.position.set(frontX, -h * 0.20, 0.03);
  group.add(hlT, hlB);

  // Taillights (rear, red)
  const rearX = -front * (w * 0.44);
  const tlGeo = new THREE.PlaneGeometry(0.09, h * 0.26);
  const tlMat = new THREE.MeshBasicMaterial({ color: 0xff1111 });
  const tlT = new THREE.Mesh(tlGeo, tlMat);
  tlT.position.set(rearX, h * 0.17, 0.03);
  const tlB = new THREE.Mesh(tlGeo, tlMat.clone());
  tlB.position.set(rearX, -h * 0.17, 0.03);
  group.add(tlT, tlB);

  // Wheels (4 dark circles)
  const wR = h * 0.17;
  const wGeo = new THREE.CircleGeometry(wR, 8);
  const wMat = new THREE.MeshBasicMaterial({ color: 0x1a1a1a });
  const rimGeo = new THREE.CircleGeometry(wR * 0.5, 8);
  const rimMat = new THREE.MeshBasicMaterial({ color: 0x555555 });
  [w * 0.30, -w * 0.30].forEach(wx => {
    [h * 0.46, -h * 0.46].forEach(wy => {
      const wheel = new THREE.Mesh(wGeo, wMat.clone());
      wheel.position.set(wx, wy, 0.015);
      group.add(wheel);
      const rim = new THREE.Mesh(rimGeo, rimMat.clone());
      rim.position.set(wx, wy, 0.016);
      group.add(rim);
    });
  });

  // Neon outline
  const outlineMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55 });
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(w, h)), outlineMat));

  return group;
}

function buildTruckMesh(cfg) {
  const { color, w, h, dir } = cfg;
  const front = dir;
  const group = new THREE.Group();

  const cabW = w * 0.36;
  const cargoW = w * 0.58;
  const cabX  =  front * (w / 2 - cabW / 2);
  const cargoX = -front * (w / 2 - cargoW / 2);

  // Cargo (back, darker shade)
  const cargoColor = new THREE.Color(color).multiplyScalar(0.55);
  const cargoMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(cargoW, h),
    new THREE.MeshBasicMaterial({ color: cargoColor })
  );
  cargoMesh.position.set(cargoX, 0, 0);
  group.add(cargoMesh);

  // Cargo markings (horizontal stripes)
  const stripeColor = new THREE.Color(color).multiplyScalar(0.75);
  for (let s = 0; s < 2; s++) {
    const stripe = new THREE.Mesh(
      new THREE.PlaneGeometry(cargoW * 0.86, h * 0.14),
      new THREE.MeshBasicMaterial({ color: stripeColor })
    );
    stripe.position.set(cargoX, (s === 0 ? 1 : -1) * h * 0.22, 0.01);
    group.add(stripe);
  }

  // Cab (front)
  const cabMat = new THREE.MeshBasicMaterial({ color });
  const cab = new THREE.Mesh(new THREE.PlaneGeometry(cabW, h), cabMat);
  cab.position.set(cabX, 0, 0.01);
  group.add(cab);

  // Cab window
  const winW = cabW * 0.68;
  const winH = h * 0.46;
  const win = new THREE.Mesh(
    new THREE.PlaneGeometry(winW, winH),
    new THREE.MeshBasicMaterial({ color: 0x88ddff, transparent: true, opacity: 0.85 })
  );
  win.position.set(cabX, h * 0.10, 0.02);
  group.add(win);

  // Divider line cab / cargo
  const divX = front * (w / 2 - cabW);
  const divGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(divX, -h / 2, 0.02),
    new THREE.Vector3(divX,  h / 2, 0.02),
  ]);
  group.add(new THREE.Line(divGeo, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 })));

  // Headlights
  const frontX = front * (w / 2 - 0.08);
  const hlGeo = new THREE.CircleGeometry(h * 0.10, 7);
  const hlMat = new THREE.MeshBasicMaterial({ color: 0xffffaa });
  const hlT = new THREE.Mesh(hlGeo, hlMat);
  hlT.position.set(frontX, h * 0.20, 0.03);
  const hlB = new THREE.Mesh(hlGeo, hlMat.clone());
  hlB.position.set(frontX, -h * 0.20, 0.03);
  group.add(hlT, hlB);

  // Taillights
  const rearX = -front * (w / 2 - 0.07);
  const tlGeo = new THREE.PlaneGeometry(0.09, h * 0.26);
  const tlMat = new THREE.MeshBasicMaterial({ color: 0xff1111 });
  const tlT = new THREE.Mesh(tlGeo, tlMat);
  tlT.position.set(rearX, h * 0.17, 0.03);
  const tlB = new THREE.Mesh(tlGeo, tlMat.clone());
  tlB.position.set(rearX, -h * 0.17, 0.03);
  group.add(tlT, tlB);

  // Wheels (6: 2 on cab, 4 on cargo)
  const wR = h * 0.17;
  const wGeo = new THREE.CircleGeometry(wR, 8);
  const wMat = new THREE.MeshBasicMaterial({ color: 0x1a1a1a });
  const rimGeo = new THREE.CircleGeometry(wR * 0.5, 8);
  const rimMat = new THREE.MeshBasicMaterial({ color: 0x555555 });
  const wheelXs = [cabX, cargoX + front * cargoW * 0.22, cargoX - front * cargoW * 0.22];
  wheelXs.forEach(wx => {
    [h * 0.46, -h * 0.46].forEach(wy => {
      const wheel = new THREE.Mesh(wGeo, wMat.clone());
      wheel.position.set(wx, wy, 0.015);
      group.add(wheel);
      const rim = new THREE.Mesh(rimGeo, rimMat.clone());
      rim.position.set(wx, wy, 0.016);
      group.add(rim);
    });
  });

  // Neon outline
  const outlineMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55 });
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(w, h)), outlineMat));

  return group;
}

function buildLogMesh(cfg) {
  const { color, w, h } = cfg;
  const group = new THREE.Group();

  // Main body
  group.add(new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({ color })
  ));

  // Wood grain lines (vertical notches)
  const grainMat = new THREE.LineBasicMaterial({ color: 0x5c3a1a, transparent: true, opacity: 0.55 });
  const numGrains = Math.max(2, Math.floor(w / 0.55));
  for (let i = 1; i < numGrains; i++) {
    const gx = -w / 2 + i * (w / numGrains);
    const grainGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(gx, -h / 2 + 0.04, 0.01),
      new THREE.Vector3(gx,  h / 2 - 0.04, 0.01),
    ]);
    group.add(new THREE.Line(grainGeo, grainMat));
  }

  // End-cap circles (darker)
  const capR = h * 0.42;
  const capColor = new THREE.Color(color).multiplyScalar(0.65);
  const capGeo = new THREE.CircleGeometry(capR, 12);
  const capMat = new THREE.MeshBasicMaterial({ color: capColor });
  const capL = new THREE.Mesh(capGeo, capMat);
  capL.position.set(-w / 2 + capR + 0.02, 0, 0.01);
  const capR2 = new THREE.Mesh(capGeo, capMat.clone());
  capR2.position.set(w / 2 - capR - 0.02, 0, 0.01);
  group.add(capL, capR2);

  // Ring on end-caps
  const ringGeo = new THREE.RingGeometry(capR * 0.45, capR * 0.6, 12);
  const ringMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(color).multiplyScalar(0.5), side: THREE.DoubleSide });
  const ringL = new THREE.Mesh(ringGeo, ringMat);
  ringL.position.set(-w / 2 + capR + 0.02, 0, 0.02);
  const ringR = new THREE.Mesh(ringGeo, ringMat.clone());
  ringR.position.set(w / 2 - capR - 0.02, 0, 0.02);
  group.add(ringL, ringR);

  // Neon outline
  const outlineMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.45 });
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.PlaneGeometry(w, h)), outlineMat));

  return group;
}

// ── Obstacle cleanup ───────────────────────────────────────────────────────────
function destroyObstacles(scene, obstacles) {
  for (const arr of Object.values(obstacles)) {
    arr.forEach(o => {
      scene.remove(o.group);
      o.group.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    });
  }
}

// ── Obstacle builder ───────────────────────────────────────────────────────────
function buildObstacles(scene, level) {
  const count = obstacleCountForLevel(level);
  const obstacles = {};
  const W = COLS * CELL;
  const hw = W / 2;
  const hh = (ROWS * CELL) / 2;

  for (const [rowStr, cfg] of Object.entries(LANE_CFG)) {
    const row = parseInt(rowStr);
    const arr = [];
    for (let i = 0; i < count; i++) {
      const startX = -hw + (i / count) * W;
      const y = -hh + row * CELL + CELL / 2;

      let group;
      if (cfg.type === 'car')        group = buildCarMesh(cfg);
      else if (cfg.type === 'truck') group = buildTruckMesh(cfg);
      else                           group = buildLogMesh(cfg);

      group.position.set(startX, y, 0.05);
      scene.add(group);

      arr.push({ group, x: startX, row, cfg, w: cfg.w });
    }
    obstacles[row] = arr;
  }
  return obstacles;
}

// ── Frog builder ───────────────────────────────────────────────────────────────
function buildFrog(scene) {
  const group = new THREE.Group();
  const G  = 0x2d6e35;   // main green
  const DG = 0x1a4a20;   // dark green (legs/underbelly)
  const BR = 0x4a2800;   // brown border

  // Back legs (bottom, splayed) — drawn first so they sit behind the body
  const backLegGeo  = new THREE.PlaneGeometry(0.28, 0.14);
  const backFeetGeo = new THREE.PlaneGeometry(0.20, 0.10);
  const legMat = new THREE.MeshBasicMaterial({ color: DG });

  const legBL = new THREE.Mesh(backLegGeo, legMat);
  legBL.position.set(-0.26, -0.32, 0.00); legBL.rotation.z =  0.45;
  const legBR = new THREE.Mesh(backLegGeo, legMat.clone());
  legBR.position.set( 0.26, -0.32, 0.00); legBR.rotation.z = -0.45;

  const footBL = new THREE.Mesh(backFeetGeo, legMat.clone());
  footBL.position.set(-0.40, -0.40, 0.00); footBL.rotation.z = -0.25;
  const footBR = new THREE.Mesh(backFeetGeo, legMat.clone());
  footBR.position.set( 0.40, -0.40, 0.00); footBR.rotation.z =  0.25;

  group.add(legBL, legBR, footBL, footBR);

  // Brown silhouette border
  group.add(new THREE.Mesh(
    new THREE.PlaneGeometry(0.72, 0.70),
    new THREE.MeshBasicMaterial({ color: BR })
  ));

  // Main body (slightly flattened ellipse via scale)
  const bodyMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.64, 0.50),
    new THREE.MeshBasicMaterial({ color: G })
  );
  bodyMesh.position.set(0, -0.06, 0.01);
  group.add(bodyMesh);

  // Belly highlight stripe
  const belly = new THREE.Mesh(
    new THREE.PlaneGeometry(0.26, 0.34),
    new THREE.MeshBasicMaterial({ color: 0x4da858, transparent: true, opacity: 0.55 })
  );
  belly.position.set(0, -0.10, 0.02);
  group.add(belly);

  // Front legs (sides of body)
  const frontLegGeo = new THREE.PlaneGeometry(0.16, 0.10);
  const flegMat = new THREE.MeshBasicMaterial({ color: DG });
  const legFL = new THREE.Mesh(frontLegGeo, flegMat);
  legFL.position.set(-0.36, 0.02, 0.01); legFL.rotation.z =  0.35;
  const legFR = new THREE.Mesh(frontLegGeo, flegMat.clone());
  legFR.position.set( 0.36, 0.02, 0.01); legFR.rotation.z = -0.35;
  group.add(legFL, legFR);

  // Head (larger circle, on top of body)
  const headMesh = new THREE.Mesh(
    new THREE.CircleGeometry(0.24, 14),
    new THREE.MeshBasicMaterial({ color: G })
  );
  headMesh.position.set(0, 0.22, 0.02);
  group.add(headMesh);

  // Mouth (thin dark bar)
  const mouthMesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.20, 0.035),
    new THREE.MeshBasicMaterial({ color: 0x0d3210 })
  );
  mouthMesh.position.set(0, 0.10, 0.03);
  group.add(mouthMesh);

  // Eye sockets (brown rings give depth)
  const eyeSocketGeo = new THREE.CircleGeometry(0.135, 10);
  const socketMat = new THREE.MeshBasicMaterial({ color: BR });
  const sockL = new THREE.Mesh(eyeSocketGeo, socketMat);
  sockL.position.set(-0.21, 0.34, 0.025);
  const sockR = new THREE.Mesh(eyeSocketGeo, socketMat.clone());
  sockR.position.set( 0.21, 0.34, 0.025);
  group.add(sockL, sockR);

  // Iris (yellow-green)
  const irisGeo = new THREE.CircleGeometry(0.11, 10);
  const irisMat = new THREE.MeshBasicMaterial({ color: 0xc8e060 });
  const irisL = new THREE.Mesh(irisGeo, irisMat);
  irisL.position.set(-0.21, 0.34, 0.03);
  const irisR = new THREE.Mesh(irisGeo, irisMat.clone());
  irisR.position.set( 0.21, 0.34, 0.03);
  group.add(irisL, irisR);

  // Pupils
  const pupilGeo = new THREE.CircleGeometry(0.058, 8);
  const pupilMat = new THREE.MeshBasicMaterial({ color: 0x060f06 });
  const pupilL = new THREE.Mesh(pupilGeo, pupilMat);
  pupilL.position.set(-0.21, 0.33, 0.04);
  const pupilR = new THREE.Mesh(pupilGeo, pupilMat.clone());
  pupilR.position.set( 0.21, 0.33, 0.04);
  group.add(pupilL, pupilR);

  // Eye shine
  const shineGeo = new THREE.CircleGeometry(0.024, 6);
  const shineMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const shineL = new THREE.Mesh(shineGeo, shineMat);
  shineL.position.set(-0.195, 0.345, 0.045);
  const shineR = new THREE.Mesh(shineGeo, shineMat.clone());
  shineR.position.set( 0.225, 0.345, 0.045);
  group.add(shineL, shineR);

  group.position.set(0, -(ROWS * CELL) / 2 + CELL / 2, 0.1);
  scene.add(group);
  return group;
}

// ── Audio helpers ──────────────────────────────────────────────────────────────
// Single shared AudioContext (browsers require user gesture before creation).
let _sfxCtx = null;
function sfxCtx() {
  if (!_sfxCtx || _sfxCtx.state === 'closed') _sfxCtx = new AudioContext();
  if (_sfxCtx.state === 'suspended') _sfxCtx.resume();
  return _sfxCtx;
}
// Plays a single square-wave blip at `freq` Hz starting at `t`, lasting `dur` seconds.
function blip(ctx, freq, t, dur, vol = 0.22) {
  const osc  = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'square';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(vol, t);
  gain.gain.setValueAtTime(0,   t + dur);
  osc.start(t);
  osc.stop(t + dur + 0.01);
}

// Short two-blip bloop when the frog jumps.
function playJumpSound() {
  try {
    const ctx = sfxCtx();
    const t = ctx.currentTime;
    blip(ctx, 280, t,        0.04, 0.18);
    blip(ctx, 420, t + 0.04, 0.04, 0.14);
  } catch(e) {}
}

// Descending 4-note "game-over" jingle when the frog dies.
function playDeathSound() {
  try {
    const ctx = sfxCtx();
    const t = ctx.currentTime;
    [440, 330, 220, 110].forEach((f, i) => blip(ctx, f, t + i * 0.11, 0.10, 0.20));
  } catch(e) {}
}

// Short ascending 3-note chime when the frog lands on a pad.
function playPadSound() {
  try {
    const ctx = sfxCtx();
    const t = ctx.currentTime;
    [523, 659, 784].forEach((f, i) => blip(ctx, f, t + i * 0.09, 0.08, 0.20));
  } catch(e) {}
}

// Ascending 6-note fanfare on level up.
function playLevelUpSound() {
  try {
    const ctx = sfxCtx();
    const t = ctx.currentTime;
    [262, 330, 392, 523, 659, 784].forEach((f, i) => blip(ctx, f, t + i * 0.10, 0.09, 0.22));
  } catch(e) {}
}

// ── Particle system ────────────────────────────────────────────────────────────
function spawnParticles(scene, x, y, color, count = 12) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const geo = new THREE.CircleGeometry(0.08 + Math.random() * 0.1, 6);
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 });
    const m = new THREE.Mesh(geo, mat);
    const angle = (i / count) * Math.PI * 2;
    const spd = 0.04 + Math.random() * 0.06;
    m.position.set(x, y, 0.2);
    scene.add(m);
    particles.push({ mesh: m, vx: Math.cos(angle) * spd, vy: Math.sin(angle) * spd, life: 1 });
  }
  return particles;
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function JimmyFrogGame() {
  const mountRef     = useRef(null);
  const stateRef     = useRef(null);
  const rafRef       = useRef(null);
  const frogAnimRef  = useRef({ active: false, t: 0, dx: 0, dy: 0 });

  const [phase,        setPhase]        = useState("start");
  const [score,        setScore]        = useState(0);
  const [lives,        setLives]        = useState(3);
  const [level,        setLevel]        = useState(1);
  const [hiScore,      setHiScore]      = useState(0);
  const [showDeadMsg,  setShowDeadMsg]  = useState(false);
  const [showLevelMsg, setShowLevelMsg] = useState(false);

  const phaseRef = useRef("start");
  const scoreRef = useRef(0);
  const livesRef = useRef(3);
  const levelRef = useRef(1);

  // ── Init Three.js ────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = mountRef.current;

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    // Clip anything that extends outside the 13×13 grid boundary
    const clipHW = (COLS * CELL) / 2;
    const clipHH = (ROWS * CELL) / 2;
    renderer.clippingPlanes = [
      new THREE.Plane(new THREE.Vector3( 1,  0, 0), clipHW),  // clip x < -half
      new THREE.Plane(new THREE.Vector3(-1,  0, 0), clipHW),  // clip x >  half
      new THREE.Plane(new THREE.Vector3( 0,  1, 0), clipHH),  // clip y < -half
      new THREE.Plane(new THREE.Vector3( 0, -1, 0), clipHH),  // clip y >  half
    ];
    el.appendChild(renderer.domElement);

    const aspect = el.clientWidth / el.clientHeight;
    const viewH = ROWS * CELL + 2;
    const viewW = viewH * aspect;
    const camera = new THREE.OrthographicCamera(
      -viewW / 2, viewW / 2,
       viewH / 2, -viewH / 2,
       0.1, 100
    );
    camera.position.z = 10;

    const { scene, pads } = buildScene();
    let obstacles = buildObstacles(scene, 1);
    const frog = buildFrog(scene);

    const hh = (ROWS * CELL) / 2;

    // Frog grid state
    const frogState = {
      col: Math.floor(COLS / 2),
      row: 0,
      moving: false,
      onLog: null,
      dead: false,
      reached: Array(5).fill(false),
    };

    const frogToWorld = (col, row) => ({
      x: -hh + col * CELL + CELL / 2,
      y: -hh + row * CELL + CELL / 2,
    });

    const resetFrog = () => {
      frogState.col = Math.floor(COLS / 2);
      frogState.row = 0;
      frogState.onLog = null;
      frogState.dead = false;
      const { x, y } = frogToWorld(frogState.col, frogState.row);
      frog.position.set(x, y, 0.1);
      frog.rotation.z = 0;
      frog.scale.set(1, 1, 1);
      frogAnimRef.current.active = false;
      frog.visible = true;
    };

    resetFrog();

    let particles = [];

    const killFrog = (color = 0xff0044) => {
      if (frogState.dead) return;
      frogState.dead = true;
      particles.push(...spawnParticles(scene, frog.position.x, frog.position.y, color));
      frog.visible = false;
      livesRef.current -= 1;
      setLives(livesRef.current);
      playDeathSound();
      setShowDeadMsg(true);
      setTimeout(() => {
        setShowDeadMsg(false);
        if (livesRef.current <= 0) {
          setHiScore(h => Math.max(h, scoreRef.current));
          phaseRef.current = "dead";
          setPhase("dead");
        } else {
          resetFrog();
        }
      }, 900);
    };

    // Rebuilds obstacles for a new level, disposing old meshes first.
    const rebuildObstacles = (newLevel) => {
      destroyObstacles(scene, stateRef.current.obstacles);
      const newObs = buildObstacles(scene, newLevel);
      stateRef.current.obstacles = newObs;
    };

    stateRef.current = {
      renderer, camera, scene, frog, frogState, obstacles, pads,
      frogToWorld, resetFrog, killFrog, particles, rebuildObstacles,
    };

    // ── Resize ──────────────────────────────────────────────────────────────────
    const onResize = () => {
      renderer.setSize(el.clientWidth, el.clientHeight);
      const a = el.clientWidth / el.clientHeight;
      const vW = viewH * a;
      camera.left   = -vW / 2;
      camera.right  =  vW / 2;
      camera.top    =  viewH / 2;
      camera.bottom = -viewH / 2;
      camera.updateProjectionMatrix();
    };
    onResize();
    window.addEventListener("resize", onResize);

    // ── Game loop ────────────────────────────────────────────────────────────────
    const animate = () => {
      rafRef.current = requestAnimationFrame(animate);
      const st = stateRef.current;
      if (!st) return;

      const { frogState: fs, obstacles: obs, pads: padsArr, killFrog: kill } = st;
      const speedMult = 1 + (levelRef.current - 1) * 0.15;

      // Move obstacles
      for (const [rowStr, arr] of Object.entries(obs)) {
        const row = parseInt(rowStr);
        const cfg = LANE_CFG[row];
        const totalW = COLS * CELL;
        const hw = totalW / 2;
        arr.forEach(o => {
          o.x += cfg.dir * cfg.speed * speedMult;
          if (cfg.dir > 0 && o.x > hw + o.w)  o.x = -hw - o.w;
          if (cfg.dir < 0 && o.x < -hw - o.w) o.x = hw + o.w;
          o.group.position.x = o.x;
        });
      }

      // Update particles
      particles = particles.filter(p => {
        p.mesh.position.x += p.vx;
        p.mesh.position.y += p.vy;
        p.life -= 0.04;
        p.mesh.material.opacity = p.life;
        if (p.life <= 0) { scene.remove(p.mesh); return false; }
        return true;
      });
      st.particles = particles;

      // Frog on log: drift with log
      if (!fs.dead && RIVER_ROWS.includes(fs.row) && fs.onLog) {
        const newFrogX = frog.position.x + LANE_CFG[fs.row].dir * LANE_CFG[fs.row].speed * speedMult;
        frog.position.x = newFrogX;
        const halfW = (COLS * CELL) / 2;
        fs.col = Math.round((frog.position.x + halfW - CELL / 2) / CELL);
        if (fs.col < 0 || fs.col >= COLS) kill(0x0066ff);
      }

      if (!fs.dead && phaseRef.current === "playing") {
        const fx = frog.position.x;
        const fy = frog.position.y;

        // Check road collision
        if (ROAD_ROWS.includes(fs.row)) {
          for (const o of obs[fs.row]) {
            if (Math.abs(fx - o.x) < (o.w / 2 + 0.28) && Math.abs(fy - o.group.position.y) < 0.4) {
              kill(0xff0033);
              break;
            }
          }
        }

        // Check river: must be on a log
        if (RIVER_ROWS.includes(fs.row)) {
          let onALog = false;
          for (const o of obs[fs.row]) {
            if (Math.abs(fx - o.x) < (o.w / 2 - 0.05) && Math.abs(fy - o.group.position.y) < 0.35) {
              onALog = true;
              fs.onLog = o;
              break;
            }
          }
          if (!onALog) { fs.onLog = null; kill(0x0066ff); }
        }

        // Check goal row
        if (fs.row === GOAL_ROW && !fs.dead) {
          // Find nearest pad
          let nearest = null;
          let minD = 999;
          padsArr.forEach((pad, i) => {
            const d = Math.abs(fx - pad.x);
            if (d < minD) { minD = d; nearest = { pad, i }; }
          });
          if (nearest && minD < 0.45 && !nearest.pad.reached) {
            // Land on a free pad: mark occupied and show mini frog
            nearest.pad.reached = true;
            nearest.pad.padMesh.material.color.set(0xffcc00);
            nearest.pad.ring.material.color.set(0xffcc00);
            nearest.pad.ring.material.opacity = 0.9;
            nearest.pad.frogling.visible = true;
            scoreRef.current += 50 * levelRef.current;
            setScore(scoreRef.current);
            playPadSound();
            resetFrog();
            // Check all pads reached → level up
            if (padsArr.every(p => p.reached)) {
              levelRef.current += 1;
              setLevel(levelRef.current);
              padsArr.forEach(p => {
                p.reached = false;
                p.padMesh.material.color.set(0x00ff44);
                p.ring.material.color.set(0x00ff44);
                p.ring.material.opacity = 0.5;
                p.frogling.visible = false;
              });
              playLevelUpSound();
              setShowLevelMsg(true);
              setTimeout(() => setShowLevelMsg(false), 2200);
              st.rebuildObstacles(levelRef.current);
              resetFrog();
            }
          } else if (nearest && minD < 0.45 && nearest.pad.reached) {
            // Pad already occupied — can't enter, kill the frog
            kill(0xff4400);
          } else if (nearest && minD >= 0.45) {
            kill(0x004400);
          }
        }
      }

      // Frog jump animation (squash & stretch)
      const fa = frogAnimRef.current;
      if (fa.active && !fs.dead) {
        fa.t += 0.14;
        if (fa.t < 0.35) {
          // Coil: flatten in movement direction
          const sx = fa.dx !== 0 ? 0.72 : 1.12;
          const sy = fa.dy !== 0 ? 0.72 : 1.12;
          frog.scale.set(sx, sy, 1);
        } else if (fa.t < 0.70) {
          // Stretch: elongate in movement direction
          const sx = fa.dx !== 0 ? 1.22 : 0.82;
          const sy = fa.dy !== 0 ? 1.22 : 0.82;
          frog.scale.set(sx, sy, 1);
        } else if (fa.t < 0.90) {
          // Land: brief squash
          const sx = fa.dy !== 0 ? 1.14 : 1.0;
          const sy = fa.dy !== 0 ? 0.82 : 1.0;
          frog.scale.set(sx, sy, 1);
        } else {
          frog.scale.set(1, 1, 1);
          fa.active = false;
        }
      } else if (!fa.active && !fs.dead) {
        frog.scale.set(1, 1, 1);
      }

      // Scanline pulsing on pad rings
      const t = Date.now() * 0.002;
      padsArr.forEach((pad, i) => {
        if (!pad.reached) pad.ring.material.opacity = 0.3 + 0.2 * Math.sin(t + i);
      });

      renderer.render(scene, camera);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
    };
  }, []);

  // ── Move frog ──────────────────────────────────────────────────────────────
  const moveFrog = useCallback((dx, dy) => {
    const st = stateRef.current;
    if (!st) return;
    const { frogState: fs, frogToWorld } = st;
    const { frog } = st;
    if (fs.dead || phaseRef.current !== "playing") return;

    // On horizontal river moves the drift has shifted frog.position.x off the grid
    // center, so using fs.col+dx snaps to a destination that is visually 1.5–2 cells
    // away. Instead, add exactly CELL to the visual position and derive the column
    // from the result — always a 1-cell visual jump.
    const onRiverHoriz = dx !== 0 && RIVER_ROWS.includes(fs.row);
    let newCol, newRow, newX, newY;

    if (onRiverHoriz) {
      newX   = frog.position.x + dx * CELL;
      newY   = frog.position.y;   // y unchanged for horizontal move
      newCol = Math.round((newX + (COLS * CELL) / 2 - CELL / 2) / CELL);
      newRow = fs.row;
    } else {
      newCol = fs.col + dx;
      newRow = fs.row + dy;
      const pos = frogToWorld(newCol, newRow);
      newX = pos.x;
      newY = pos.y;
    }

    if (newCol < 0 || newCol >= COLS) return;
    if (newRow < 0) return;
    if (newRow > GOAL_ROW) return;

    fs.col = newCol;
    fs.row = newRow;
    fs.onLog = null;

    frog.position.x = newX;
    frog.position.y = newY;

    // Rotate frog to face movement direction (eyes at top = up = default)
    if      (dy > 0)  frog.rotation.z = 0;
    else if (dy < 0)  frog.rotation.z = Math.PI;
    else if (dx > 0)  frog.rotation.z = -Math.PI / 2;
    else if (dx < 0)  frog.rotation.z =  Math.PI / 2;

    // Trigger squash & stretch jump animation and sound
    frogAnimRef.current = { active: true, t: 0, dx, dy };
    playJumpSound();

    // Score for moving forward
    if (dy > 0) {
      scoreRef.current += 10;
      setScore(scoreRef.current);
    }
  }, []);

  // ── Start game ─────────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    const st = stateRef.current;
    if (!st) return;
    scoreRef.current = 0;
    livesRef.current = 3;
    levelRef.current = 1;
    setScore(0);
    setLives(3);
    setLevel(1);
    st.pads.forEach(p => {
      p.reached = false;
      p.padMesh.material.color.set(0x00ff44);
      p.ring.material.color.set(0x00ff44);
      p.ring.material.opacity = 0.5;
      p.frogling.visible = false;
    });
    st.rebuildObstacles(1);
    st.resetFrog();
    phaseRef.current = "playing";
    setPhase("playing");
  }, []);

  // ── Keyboard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      switch (e.key) {
        case "ArrowUp":    case "w": case "W": e.preventDefault(); moveFrog( 0,  1); break;
        case "ArrowDown":  case "s": case "S": e.preventDefault(); moveFrog( 0, -1); break;
        case "ArrowLeft":  case "a": case "A": e.preventDefault(); moveFrog(-1,  0); break;
        case "ArrowRight": case "d": case "D": e.preventDefault(); moveFrog( 1,  0); break;
        case " ": case "Enter":
          if (phaseRef.current === "start" || phaseRef.current === "dead") startGame();
          break;
        default: break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [moveFrog, startGame]);

  // ── Touch swipe ────────────────────────────────────────────────────────────
  const touchRef = useRef(null);
  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  }, []);
  const onTouchEnd = useCallback((e) => {
    if (!touchRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    touchRef.current = null;
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      if (phaseRef.current === "start" || phaseRef.current === "dead") startGame();
      return;
    }
    if (Math.abs(dx) > Math.abs(dy)) {
      moveFrog(dx > 0 ? 1 : -1, 0);
    } else {
      moveFrog(0, dy > 0 ? -1 : 1);
    }
  }, [moveFrog, startGame]);

  // ── D-pad button ───────────────────────────────────────────────────────────
  const DBtn = ({ dx, dy, label }) => (
    <button
      onPointerDown={(e) => { e.preventDefault(); moveFrog(dx, dy); }}
      style={{
        width: 52, height: 52, borderRadius: 8,
        background: "rgba(0,255,136,0.1)",
        border: "1.5px solid rgba(0,255,136,0.5)",
        color: "#00ff88", fontSize: 20,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", touchAction: "none", userSelect: "none",
        boxShadow: "0 0 8px rgba(0,255,136,0.3)",
      }}
    >{label}</button>
  );

  const fmt = n => String(n).padStart(5, "0");

  const HEARTS = ["♥", "♥", "♥"];

  return (
    <div
      style={{ width: "100vw", height: "100svh", background: "#0a0015", overflow: "hidden",
        position: "relative", fontFamily: "'Courier New', monospace" }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Three.js canvas mount */}
      <div ref={mountRef} style={{ position: "absolute", inset: 0 }} />

      {/* Vignette */}
      <div style={{ position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none",
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)" }} />

      {/* HUD */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, zIndex: 10,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "10px 20px",
        background: "linear-gradient(180deg, rgba(10,0,21,0.95) 0%, transparent 100%)",
      }}>
        <div>
          <div style={{ fontSize: 9, color: "#ff00aa", letterSpacing: 4, textShadow: "0 0 8px #ff00aa" }}>HI</div>
          <div style={{ fontSize: 22, color: "#ff00aa", fontWeight: 900, textShadow: "0 0 16px #ff00aa" }}>{fmt(hiScore)}</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontSize: "clamp(18px, 4vw, 30px)", fontWeight: 900, letterSpacing: 6,
            background: "linear-gradient(180deg, #00ff88 0%, #ff00aa 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 8px #00ff88)",
          }}>JIMMY FROG</div>
          <div style={{ fontSize: 10, color: "#00ffcc", letterSpacing: 3, textShadow: "0 0 6px #00ffcc" }}>
            LVL {level} &nbsp;
            {HEARTS.map((h, i) => (
              <span key={i} style={{ color: i < lives ? "#ff0055" : "#330011", textShadow: i < lives ? "0 0 8px #ff0055" : "none" }}>{h}</span>
            ))}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 9, color: "#00ffcc", letterSpacing: 4, textShadow: "0 0 8px #00ffcc" }}>SCORE</div>
          <div style={{ fontSize: 22, color: "#00ffcc", fontWeight: 900, textShadow: "0 0 16px #00ffcc" }}>{fmt(score)}</div>
        </div>
      </div>

      {/* START screen */}
      {phase === "start" && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 20,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(10,0,21,0.82)",
        }}>
          <div style={{
            fontSize: "clamp(42px, 12vw, 90px)", fontWeight: 900, letterSpacing: 8,
            background: "linear-gradient(180deg, #00ff88 0%, #ff00aa 100%)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 24px #00ff88) drop-shadow(0 0 48px #ff00aa)",
            animation: "jfPulse 2s ease-in-out infinite", marginBottom: 4,
          }}>JIMMY FROG</div>
          <div style={{ color: "#ff00cc", fontSize: 11, letterSpacing: 6, marginBottom: 36,
            textShadow: "0 0 12px #ff00cc" }}>◆ SYNTHWAVE FROGGER ◆</div>
          <div style={{
            border: "1px solid rgba(0,255,136,0.3)", padding: "16px 36px",
            borderRadius: 4, marginBottom: 28, textAlign: "center",
            background: "rgba(0,255,136,0.04)",
          }}>
            <div style={{ color: "#00ff88", fontSize: 10, letterSpacing: 4, marginBottom: 10,
              textShadow: "0 0 8px #00ff88" }}>CONTROLS</div>
            <div style={{ color: "#887799", fontSize: 11, lineHeight: 2.2, letterSpacing: 1 }}>
              🎮 ARROW KEYS · WASD<br/>📱 SWIPE · D-PAD<br/>🐸 REACH ALL 5 PADS TO LEVEL UP
            </div>
          </div>
          <button onClick={startGame} style={{
            background: "linear-gradient(135deg, #00ff88, #00ccff)", border: "none",
            color: "#0a0015", fontSize: 14, letterSpacing: 5, padding: "14px 52px",
            borderRadius: 4, cursor: "pointer", fontFamily: "'Courier New', monospace",
            fontWeight: 900, textTransform: "uppercase",
            boxShadow: "0 0 36px rgba(0,255,136,0.8)",
          }}>▶ START</button>
        </div>
      )}

      {/* GAME OVER screen */}
      {phase === "dead" && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 20,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(10,0,21,0.85)",
        }}>
          <div style={{
            fontSize: "clamp(36px, 10vw, 72px)", fontWeight: 900, letterSpacing: 5,
            color: "#ff0044", marginBottom: 8,
            textShadow: "0 0 20px #ff0044, 0 0 50px #ff0044",
            animation: "jfFlicker 0.5s ease-in-out infinite alternate",
          }}>GAME OVER</div>
          <div style={{ color: "#ff00aa", fontSize: 11, letterSpacing: 5, marginBottom: 36,
            textShadow: "0 0 10px #ff00aa" }}>— RIBBIT! —</div>
          <div style={{
            display: "flex", gap: 48, marginBottom: 36,
            border: "1px solid rgba(0,255,136,0.2)", padding: "18px 48px",
            borderRadius: 4, background: "rgba(0,255,136,0.03)",
          }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#554466", fontSize: 9, letterSpacing: 4, marginBottom: 4 }}>SCORE</div>
              <div style={{ color: "#00ff88", fontSize: 32, fontWeight: 900, textShadow: "0 0 16px #00ff88" }}>{score}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#554466", fontSize: 9, letterSpacing: 4, marginBottom: 4 }}>BEST</div>
              <div style={{ color: "#ff00aa", fontSize: 32, fontWeight: 900, textShadow: "0 0 16px #ff00aa" }}>{hiScore}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#554466", fontSize: 9, letterSpacing: 4, marginBottom: 4 }}>LEVEL</div>
              <div style={{ color: "#00ffcc", fontSize: 32, fontWeight: 900, textShadow: "0 0 16px #00ffcc" }}>{level}</div>
            </div>
          </div>
          <button onClick={startGame} style={{
            background: "linear-gradient(135deg, #ff0044, #ff00aa)", border: "none",
            color: "#fff", fontSize: 14, letterSpacing: 5, padding: "14px 52px",
            borderRadius: 4, cursor: "pointer", fontFamily: "'Courier New', monospace",
            fontWeight: 900, textTransform: "uppercase",
            boxShadow: "0 0 36px rgba(255,0,68,0.8)",
          }}>▶ TRY AGAIN</button>
        </div>
      )}

      {/* YOU'RE DEAD overlay */}
      {showDeadMsg && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 18,
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none",
        }}>
          <div style={{
            fontSize: "clamp(32px, 9vw, 64px)", fontWeight: 900, letterSpacing: 4,
            color: "#ff0033",
            textShadow: "0 0 16px #ff0033, 0 0 40px #ff0033, 0 0 80px #ff6600",
            animation: "jfDeadPop 0.18s ease-out",
          }}>YOU'RE DEAD!</div>
        </div>
      )}

      {/* LEVEL UP overlay */}
      {showLevelMsg && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 18,
          display: "flex", alignItems: "center", justifyContent: "center",
          pointerEvents: "none",
        }}>
          <div style={{ textAlign: "center", animation: "jfLevelPop 0.22s ease-out" }}>
            <div style={{
              fontSize: "clamp(26px, 7vw, 52px)", fontWeight: 900, letterSpacing: 3,
              background: "linear-gradient(135deg, #00ff88, #ffee00)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 0 14px #00ff88) drop-shadow(0 0 28px #ffee00)",
            }}>Congratulations!</div>
            <div style={{
              fontSize: "clamp(12px, 3vw, 20px)", color: "#00ffcc", letterSpacing: 4,
              marginTop: 8, textShadow: "0 0 10px #00ffcc",
            }}>Moving to next level!</div>
          </div>
        </div>
      )}

      {/* D-PAD */}
      {phase === "playing" && (
        <div style={{
          position: "absolute", bottom: 20, right: 20, zIndex: 15,
          display: "grid",
          gridTemplateColumns: "52px 52px 52px",
          gridTemplateRows: "52px 52px 52px",
          gap: 5,
        }}>
          <span /><DBtn dx={0} dy={1} label="▲" /><span />
          <DBtn dx={-1} dy={0} label="◀" />
          <div style={{ width: 52, height: 52, borderRadius: 8,
            border: "1px solid rgba(0,255,136,0.15)", background: "rgba(0,255,136,0.03)" }} />
          <DBtn dx={1} dy={0} label="▶" />
          <span /><DBtn dx={0} dy={-1} label="▼" /><span />
        </div>
      )}

      <style>{`
        @keyframes jfPulse {
          0%,100% { filter: drop-shadow(0 0 20px #00ff88) drop-shadow(0 0 40px #ff00aa); }
          50%      { filter: drop-shadow(0 0 40px #00ff88) drop-shadow(0 0 80px #ff00aa); }
        }
        @keyframes jfFlicker {
          0%   { opacity: 1; }
          90%  { opacity: 1; }
          92%  { opacity: 0.2; }
          94%  { opacity: 1; }
          98%  { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes jfDeadPop {
          0%   { transform: scale(0.4); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes jfLevelPop {
          0%   { transform: scale(0.5) translateY(20px); opacity: 0; }
          65%  { transform: scale(1.08) translateY(-4px); opacity: 1; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { overflow: hidden; background: #0a0015; }
        button:focus { outline: none; }
      `}</style>
    </div>
  );
}
